import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, checkPlatformAdmin } from '../../lib/tenant';
import { supabase } from '../../lib/supabase';
import { Shield, LogIn, Loader2, AlertCircle } from 'lucide-react';
export function GlobalAdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    loading,
    signIn
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Where to redirect after login — preserve intended destination
  const from = (location.state as any)?.from || '/admin/enrollment';
  // If already logged in, check role and redirect
  useEffect(() => {
    if (!loading && user) {
      checkPlatformAdmin(user.id).then(isPlatformAdmin => {
        if (isPlatformAdmin) {
          navigate(from, {
            replace: true
          });
        } else {
          // Check if user has restaurant access
          supabase.from('restaurant_users').select('role, restaurant_id').eq('user_id', user.id).eq('is_active', true).limit(1).maybeSingle().then(({
            data
          }) => {
            if (data) {
              navigate(from, {
                replace: true
              });
            } else {
              navigate('/', {
                replace: true
              });
            }
          });
        }
      });
    }
  }, [user, loading, navigate, from]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    console.log('[AdminLogin] Attempting sign in...', {
      email: email.trim().toLowerCase(),
      passwordLength: password.length,
      redirectTo: from
    });
    try {
      await signIn(email.trim().toLowerCase(), password);
      // After sign in, check platform admin status to determine redirect
      const {
        data: {
          user: loggedInUser
        }
      } = await supabase.auth.getUser();
      if (loggedInUser) {
        const isPlatformAdmin = await checkPlatformAdmin(loggedInUser.id);
        const redirectTo = isPlatformAdmin ? from : '/';
        console.log('[AdminLogin] Sign in SUCCESS — redirecting to:', redirectTo);
        navigate(redirectTo, {
          replace: true
        });
      } else {
        console.log('[AdminLogin] Sign in SUCCESS — redirecting to:', from);
        navigate(from, {
          replace: true
        });
      }
    } catch (err: any) {
      console.error('[AdminLogin] Sign in FAILED:', {
        message: err.message,
        status: err.status,
        name: err.name,
        fullError: err
      });
      // Provide specific guidance for known errors
      if (err.message?.includes('Invalid API key')) {
        setError('Error de configuración: API key inválida. Contacta al administrador del sistema.');
      } else if (err.message?.includes('Invalid login credentials')) {
        setError('Correo o contraseña incorrectos.');
      } else {
        setError(err.message || 'Error al iniciar sesión.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-4851" data-mp="vcus0cuts8ax">
        <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-4852" data-mp="vb1kkvce1k1v" />
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex items-center justify-center p-6" data-id="element-4853" data-mp="105e8piot7jjy">
      <div className="max-w-sm w-full space-y-8" data-id="element-4854" data-mp="qdro4vql084j">
        <div className="text-center space-y-3" data-id="element-4855" data-mp="qejk47o3c7vc">
          <div className="w-16 h-16 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center mx-auto" data-id="element-4856" data-mp="ce0ef773ldwc">
            <Shield className="w-8 h-8 text-[#006847]" data-id="element-4857" data-mp="hdd19rxyykyx" />
          </div>
          <h1 className="text-2xl font-black tracking-tight" data-id="element-4858" data-mp="9ce1moq8f4bl">NOIZA Admin</h1>
          <p className="text-white/40 text-sm" data-id="element-4859" data-mp="9ei3z28hfv9r">
            Inicia sesión para acceder al panel de administración
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-4860" data-mp="3vrglv3rl1hfq">
          <div data-id="element-4861" data-mp="3vpvvzrg7qmo7">
            <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-4862" data-mp="3rqd7klp6ntgm">
              Correo electrónico
            </label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@noiza.com" className="w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-4 text-base text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" required autoFocus data-id="element-4863" data-mp="3msv8ro4vqumc" />
          </div>

          <div data-id="element-4864" data-mp="16zmlkfal45bh">
            <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-4865" data-mp="1717fq3szlm8t">
              Contraseña
            </label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-4 text-base text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" required data-id="element-4866" data-mp="2ovyb8lbkrnjr" />
          </div>

          {error && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-red-400 text-sm" data-id="element-4867" data-mp="2ayjib6m44xn1">
              <AlertCircle className="w-4 h-4 shrink-0" data-id="element-4868" data-mp="2f1xypydyrg10" />
              {error}
            </div>}

          <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-bold text-base rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-4869" data-mp="3qes2f1jxx09z">
            {isSubmitting ? <>
                <Loader2 className="w-5 h-5 animate-spin" data-id="element-4870" data-mp="1r9gxlhvy1b3x" />
                Iniciando sesión...
              </> : <>
                <LogIn className="w-5 h-5" data-id="element-4871" data-mp="20ay9sp68csnc" />
                Iniciar sesión
              </>}
          </button>
        </form>
      </div>
    </div>;
}